import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchCoffeeRecords, CoffeeRecord } from '../utils/api';
import { usePageMeta } from '../utils/usePageMeta';
import { Eyebrow } from '../components/Eyebrow';

export const Coffees: React.FC = () => {
  usePageMeta({
    title: 'Our Coffees — Kenyan Specialty Coffee Catalog | Lot 20',
    description: 'Browse our current specialty coffee offerings from Kenya. Anaerobic naturals, co-ferments, and experimental lots. Scoring 85-96+ points.',
    canonicalPath: '/coffees'
  });

  const [coffees, setCoffees] = useState<CoffeeRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchCoffeeRecords()
      .then(records => setCoffees(records))
      .catch(() => setCoffees([]))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <>
      {/* Page Header */}
      <section className="pt-3 pb-16 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <Eyebrow className="mb-3">Catalog</Eyebrow>
          <h1 className="font-display text-4xl md:text-5xl text-[#404040] mb-4">
            Current Coffees
          </h1>
          <p className="text-lg text-[#6B6B6B] max-w-2xl">
            Our latest lineup — fresh, fully traceable, and experimentally processed
            from our own factories across the western highlands.
          </p>
        </div>
      </section>

      {/* Coffee Grid */}
      <section className="pb-24 md:pb-32 bg-[#FAFAF8]">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          {isLoading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[4/5] bg-[#F0EDE8] mb-5" />
                  <div className="h-3 bg-[#F0EDE8] rounded w-24 mb-3" />
                  <div className="h-5 bg-[#F0EDE8] rounded w-48 mb-2" />
                  <div className="h-4 bg-[#F0EDE8] rounded w-full" />
                </div>
              ))}
            </div>
          ) : coffees.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-[#A3A3A3]">No coffees available right now. Check back soon.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14">
              {coffees.map(coffee => (
                <CoffeeCard key={coffee.id} coffee={coffee} />
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
};

const CoffeeCard: React.FC<{ coffee: CoffeeRecord }> = ({ coffee }) => {
  const statusColor: Record<string, string> = {
    'AVAILABLE': 'text-green-700 bg-green-50',
    'PREORDER': 'text-amber-700 bg-amber-50',
    'SOLD OUT': 'text-[#A3A3A3] bg-[#F0EDE8]',
  };

  return (
    <Link to={`/coffee/${coffee.slug}`} className="group block">
      <div className="aspect-[4/5] overflow-hidden bg-[#F0EDE8] mb-5 relative">
        {coffee.imageUrl ? (
          <img
            src={coffee.imageUrl}
            alt={coffee.title}
            width={800}
            height={1000}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center">
            <span className="text-4xl font-display text-[#C4BEB6]">LOT 20</span>
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C4BEB6] mt-2">{coffee.processType}</span>
          </div>
        )}
        {/* Status badge */}
        <span className={`absolute top-4 left-4 text-[10px] uppercase tracking-[0.15em] font-medium px-3 py-1.5 ${statusColor[coffee.availabilityStatus] || ''}`}>
          {coffee.availabilityStatus}
        </span>
      </div>

      <p className="text-[11px] uppercase tracking-[0.15em] text-[#A3A3A3] mb-1.5">
        {coffee.processType}
      </p>
      <h2 className="text-xl font-display text-[#404040] group-hover:text-[#E85C27] transition-colors duration-300 mb-2">
        {coffee.title}
      </h2>
      <p className="font-display text-sm text-[#6B6B6B] leading-relaxed mb-3">
        {coffee.visualDescription}
      </p>

      <div className="flex items-center gap-3 text-xs text-[#A3A3A3]">
        {coffee.cuppingScore > 0 && <span>{coffee.cuppingScore} pts</span>}
        <span className="w-px h-3 bg-[#E8E6E2]" />
        <span>{coffee.tastingNotes.split(',').slice(0, 3).join(', ')}</span>
      </div>
    </Link>
  );
};
